<script setup lang="ts">
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { getInputStateClassesV2 } from '~kui/composables/useInputState';
import { SelectContent, SelectItem, SelectItemText, SelectPortal, SelectRoot, SelectTrigger, SelectValue, SelectViewport } from 'reka-ui';

import type { ComboboxProps } from '../types';

import { contentClasses, itemClasses, viewportClasses } from '../classList';
import { comboboxDefaults } from '../types';
import { useComboboxClasses, useComboboxSingle, useComboboxState, useComboboxValue } from '../useCombobox';

const props = withDefaults(defineProps<ComboboxProps>(), comboboxDefaults);

const emit = defineEmits<{
  blur: [];
}>();

const model = defineModel<string | number | null>({ required: true });

const { hasValueSingle } = useComboboxValue();
const hasValue = computed(() => hasValueSingle(model.value, props.nullable));
const { open, selected, getDisplayValue, clearSelection } = useComboboxSingle(props, model);
const { inputState } = useComboboxState(props, hasValue.value);
const { classes: v1TriggerClasses } = useComboboxClasses(inputState, 'trigger');

const elementId = useId();
const hasError = computed(() => props.errorMessages.length > 0);
const errorsId = computed(() => `error-${elementId}`);

// v2 mirrors KUICoreInputText's box (same wrapper/font classes) so selects line
// up with text inputs on the same form.
const { wrapperClasses: v2TriggerClasses, fontClasses: v2FontClasses } = getInputStateClassesV2({
  disabled: computed(() => props.disabled ?? false),
  state: computed(() => (hasError.value ? 'error' : props.inputTypeState)),
  wrapperClasses: ['cursor-pointer', 'px-4', 'text-left'],
});
const triggerClasses = computed(() =>
  props.version === 'v2' ? twMerge(v2TriggerClasses.value, v2FontClasses.value) : v1TriggerClasses.value,
);

// Emit blur when dropdown closes
watch(open, (newOpen, oldOpen) => {
  if (oldOpen === true && newOpen === false) {
    emit('blur');
  }
});

// Also emit blur when component loses focus (e.g., tabbing past)
const handleFocusOut = () => {
  emit('blur');
};
</script>

<template>
  <div class="group/kui-input" @focusout="handleFocusOut">
    <SelectRoot
      v-model="selected"
      v-model:open="open"
      :disabled="disabled"
      :data-testid="dataTestId"
    >
      <KUICoreInputLabel
        v-if="!hideLabel"
        :label="label"
        :required="required"
        :class="{ 'mb-2': version === 'v2' }"
      >
        {{ label }}
      </KUICoreInputLabel>
      <SelectTrigger
        :class="triggerClasses"
        :data-testid="`${dataTestId}-trigger`"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorsId : undefined"
      >
        <SelectValue :placeholder="placeholder" :class="{ 'text-muted-foreground': !hasValue.value }">
          <template v-if="hasValue.value">
            {{ getDisplayValue() }}
          </template>
        </SelectValue>
        <div class="ms-auto flex items-center">
          <KUICoreClickable
            v-if="clearable && hasValue.value"
            class="mx-1"
            :data-testid="`${dataTestId}-clear-button`"
            @click.stop="clearSelection"
          >
            <Icon :name="ICONS.cancel" size="xs" />
          </KUICoreClickable>
          <KUICoreButtonIcon :icon="ICONS.arrowDown" variant="tertiary" />
        </div>
      </SelectTrigger>
      <SelectPortal>
        <SelectContent
          class="max-h-[var(--reka-popper-available-height)] min-w-[var(--reka-popper-anchor-width)] z-101"
          :class="contentClasses"
          position="popper"
        >
          <SelectViewport :class="viewportClasses" :data-testid="`${dataTestId}-listbox`">
            <SelectItem
              v-for="option in options"
              :key="option.label"
              :value="option.value"
              :class="itemClasses"
            >
              <SelectItemText>{{ option.label }}</SelectItemText>
            </SelectItem>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
    <KUICoreInputErrors
      v-if="errorMessages.length"
      :id="errorsId"
      :error-messages="errorMessages"
    />
  </div>
</template>
